import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { useLiftBlock } from '../hooks/useBlockMutations'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  blockId: string
  blockNumber: string
  isRegulatory: boolean
  onLifted: () => void
}

const LIFT_REASON_MIN = 10

/**
 * AF-128 — Lift dialog. Reason is required; for regulatory blocks the
 * dialog renders an extra confirmation that the lift is happening with
 * legal-team approval (since the gate above only opens this dialog when
 * the user already has `blocks:lift_regulatory`).
 */
export function LiftDialog({
  open,
  onOpenChange,
  blockId,
  blockNumber,
  isRegulatory,
  onLifted,
}: Props) {
  const [reason, setReason] = useState('')
  const lift = useLiftBlock()

  const submit = () => {
    if (reason.trim().length < LIFT_REASON_MIN) return
    lift.mutate(
      { block_id: blockId, reason: reason.trim() },
      {
        onSuccess: () => {
          onOpenChange(false)
          setReason('')
          onLifted()
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lift block {blockNumber}</DialogTitle>
          <DialogDescription>
            Lifting takes effect immediately. The block stays in the audit
            trail with the reason you give.
          </DialogDescription>
        </DialogHeader>

        {isRegulatory && (
          <div className="rounded border border-priority-critical/30 bg-priority-critical/5 px-3 py-2 text-xs text-priority-critical">
            <strong>Regulatory block.</strong> Confirm legal team has signed
            off — this action is logged and visible to compliance auditors.
          </div>
        )}

        <div className="flex flex-col gap-1">
          <Label htmlFor="lift-reason">Reason</Label>
          <textarea
            id="lift-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this block being lifted? Surfaces in the audit trail."
            className="min-h-[100px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <p className="text-xs text-muted-fg">Min {LIFT_REASON_MIN} characters.</p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={reason.trim().length < LIFT_REASON_MIN || lift.isPending}
            onClick={submit}
          >
            {lift.isPending ? 'Lifting…' : 'Lift block'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
